import React, { useEffect, useState, useCallback, useRef } from "react";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { RadioButton } from "primereact/radiobutton";
import { FileUpload } from "primereact/fileupload";
import * as Fa from "react-icons/fa";
import { toast } from "react-toastify";
import { fetchData, postData } from "../../../Services/GlobalService";
import { GetDateFormat } from "../../../Services/GlobalFunction/GetDateFormat";
import { downloadExcel } from "../../../Services/GlobalFunction/DownLoadExcel";
import Loader from "../../../Components/Loader";
import { MultiSelect } from "primereact/multiselect";

/* ============================================================
   TYPES
============================================================ */
export type SegmentType = "B2C" | "HDSSD" | "Industrial DB";

const CHANNEL_TYPE_MAP: Record<SegmentType, string> = {
  B2C: "B2C",
  HDSSD: "HDSSD",
  "Industrial DB": "IDB",
};

export interface ConstructOption {
  label: string;
  value: number;
  startDate: string;
  endDate: string;
}

export interface LBMOption {
  label: string; // LBMBrandDesc
  value: string; // LBMBrandCode
}

export interface ProductOption {
  label: string; // ProductDesc
  value: string; // ProductCode
}

export interface SkuEntry {
  skuCode: string;
}

export interface GridRow {
  ConstructSeq: number;
  Name: string;
  Seq: number;
  IS_SKU_SELECTED: string;
  selectedLBMs: string[];
  selectedProducts: string[];
  // Per-row checkbox state: when true → dropdowns disabled + cleared, Upload btn active
  skuCheckboxChecked: boolean;
  // SKU codes uploaded via Excel for this row
  skuCodes: SkuEntry[];
}



const UserId = localStorage.getItem("UserId")?.toString();

const controllerName = "DBScoreCard/LBMBrandAndBrandMapping";
const Endpoint = {
  GetConstructData: "FetchingConstructData",
  GetGridData: "FetchingGridData",
  GetAllLBMData: "FetchingLBMAllData",
  GetSelectedLBM: "CheckedLbmCode",
  GetAllProductByLBM: "GetAllProductByLBM",
  GetSelectedProduct: "CheckedProduct",
  ExportFile: "ExportSKU",
  SavingLBMProduct: "SavingLBMProduct",
};


/* ============================================================
   UPLOAD SKU MODAL
   - Uses PrimeReact <FileUpload> for the original Choose/Upload/Cancel UI
   - On upload, parses the Excel client-side via SheetJS (no API call)
   - Extracted SKU_CODE values are stored on the row and sent in Save payload
   - Upload button is GREY (disabled) when skuCheckboxChecked is false
   - Upload button turns GREEN (active) when skuCheckboxChecked is true
============================================================ */
interface UploadModalProps {
  row: GridRow;
  onClose: () => void;
  onSkuCodesLoaded: (rowSeq: number, codes: SkuEntry[]) => void;
}

const UploadSKUModal: React.FC<UploadModalProps> = ({
  row,
  onClose,
  onSkuCodesLoaded,
}) => {
  const getformat = GetDateFormat();
  const fileUploadRef = useRef<FileUpload>(null);
  const [parsing, setParsing] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [parsedCodes, setParsedCodes] = useState<SkuEntry[]>(
    row.skuCodes ?? [],
  );
  const [parseError, setParseError] = useState<string>("");

  const uploadEnabled = row.skuCheckboxChecked;

  /* ── Download blank template ── */
  const handleDownloadTemplate = () => {
    downloadExcel([{ SKU_CODE: "" }], `SKUCode_Template_${getformat}.xlsx`);
  };

  /* ── Export existing SKU codes ── */
  const handleExport = async () => {
    setExportLoading(true);
    try {
      const res = await fetchData(
        controllerName,
        Endpoint.ExportFile,
        `ConstarctSeq=${row.ConstructSeq}&SEQ=${row.Seq}`,
      );
      if (res?.Data?.length > 0) {
        downloadExcel(res.Data, `SKUCode_Export_${getformat}.xlsx`);
      } else {
        toast.error("No Data Found");
      }
    } catch (ex: any) {
      toast.error(ex?.response?.data?.Message ?? "Export failed");
    } finally {
      setExportLoading(false);
    }
  };

  const handleFileUpload = async (event: any) => {
    if (!uploadEnabled) return;
    const file: File = event.files[0];
    if (!file) return;

    setParseError("");
    setParsing(true);

    try {
      const XLSX = await import("xlsx");
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const rows: any[] = XLSX.utils.sheet_to_json(sheet, { defval: "" });

      if (rows.length === 0) {
        setParseError("The uploaded file is empty.");
        fileUploadRef.current?.clear();
        setParsing(false);
        return;
      }

      if (!Object.prototype.hasOwnProperty.call(rows[0], "SKU_CODE")) {
        setParseError(
          'Column "SKU_CODE" not found. Please use the provided template.',
        );
        fileUploadRef.current?.clear();
        setParsing(false);
        return;
      }

      const codes: SkuEntry[] = rows
        .map((r) => ({ skuCode: String(r["SKU_CODE"]).trim() }))
        .filter((e) => e.skuCode !== "" && e.skuCode !== "undefined");

      if (codes.length === 0) {
        setParseError("No valid SKU codes found in the file.");
        fileUploadRef.current?.clear();
        setParsing(false);
        return;
      }

      setParsedCodes(codes);
      onSkuCodesLoaded(row.Seq, codes);
      toast.success(`${codes.length} SKU code(s) loaded successfully.`);
      fileUploadRef.current?.clear();
    } catch {
      setParseError(
        "Failed to read the file. Please ensure it is a valid .xlsx / .xls file.",
      );
      toast.error("File parsing failed.");
      fileUploadRef.current?.clear();
    } finally {
      setParsing(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.48)",
        zIndex: 10000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backdropFilter: "blur(2px)",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 12,
          width: "min(800px, 95vw)",
          boxShadow: "0 12px 48px rgba(0,0,0,0.2)",
          overflow: "hidden",
          animation: "modalIn 0.18s ease",
        }}
      >
        {/* Modal header */}
        <div
          style={{
            // background: "linear-gradient(135deg, #2e7d32 0%, #43a047 100%)",
            background: "#009343",
            padding: "10px 16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div>
            <div style={{ color: "#fff", fontWeight: 700, fontSize: 17 }}>
              <Fa.FaUpload style={{ marginRight: 8, fontSize: 14 }} />
              Upload SKU
            </div>
            <div
              style={{
                color: "#c8e6c9",
                fontSize: 12,
                marginTop: 2,
                maxWidth: 480,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {row.Name}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,0.15)",
              border: "none",
              color: "#fff",
              borderRadius: 6,
              width: 32,
              height: 32,
              fontSize: 20,
              lineHeight: 1,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            &times;
          </button>
        </div>

        {/* Modal body */}
        <div style={{ padding: "16px 18px" }}>
          {/* Action buttons */}

          {/* Main content: FileUpload + illustration */}
          <div style={{ display: "flex", gap: 28, alignItems: "flex-start" }}>
            <div style={{ flex: 1 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 8,
                  marginBottom: 10,
                  flexWrap: "wrap",
                }}
              >
                <button
                  type="button"
                  className="btn anime-secondary-btn"
                  onClick={handleDownloadTemplate}
                >
                  Download Template <Fa.FaDownload className="ms-2" />
                </button>
                <button
                  type="button"
                  className="btn anime-btn"
                  onClick={handleExport}
                  disabled={exportLoading}
                >
                  {exportLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" />
                      Exporting...
                    </>
                  ) : (
                    <>
                      Export <Fa.FaFileExport className="ms-2" />
                    </>
                  )}
                </button>
              </div>
              <div className="UploadSection clearfix">
                <div
                  className="conText"
                  style={{
                    marginBottom: 8,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <Fa.FaInfoCircle />
                  <span>Select an Excel File to upload</span>
                </div>

                {/* PrimeReact FileUpload — original Choose / Upload / Cancel UI.
                    customUpload fires handleFileUpload which parses the file
                    client-side; no HTTP request is made. */}
                <FileUpload
                  ref={fileUploadRef}
                  className="clearfix nTopMargin"
                  name="skuUpload[]"
                  accept=".xlsx,.xls"
                  emptyTemplate={
                    <p className="m-0">
                      Drag and drop files to here to upload.
                    </p>
                  }
                  customUpload
                  uploadHandler={handleFileUpload}
                  disabled={!uploadEnabled}
                  onClear={() => {
                    setParseError("");
                  }}
                  onRemove={() => {
                    setParseError("");
                  }}
                />

                {/* Parsing spinner */}
                {parsing && (
                  <div
                    style={{
                      marginTop: 8,
                      color: "#2e7d32",
                      fontSize: 12,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <span className="spinner-border spinner-border-sm" />
                    Reading file, please wait…
                  </div>
                )}

                {/* Parse error */}
                {parseError && (
                  <div
                    style={{
                      marginTop: 8,
                      color: "#c62828",
                      fontSize: 12,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <Fa.FaExclamationCircle />
                    {parseError}
                  </div>
                )}

                {/* Loaded SKU count badge */}
                {parsedCodes.length > 0 && !parseError && (
                  <div
                    style={{
                      marginTop: 10,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      background: "#e8f5e9",
                      color: "#2e7d32",
                      borderRadius: 20,
                      padding: "4px 12px",
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    <Fa.FaCheckCircle style={{ fontSize: 13 }} />
                    {parsedCodes.length} SKU code
                    {parsedCodes.length !== 1 ? "s" : ""} ready to save
                  </div>
                )}
              </div>
            </div>

            {/* Illustration */}
            <div style={{ flexShrink: 0 }}>
              <img
                src={require("../../../Assets/Images/File_Upload.png")}
                className="File_Upload_Img drop-right"
                alt="Upload illustration"
              />
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes modalIn {
          from { opacity: 0; transform: translateY(-16px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
};
const ChipLabel = ({
  selected,
  options,
  placeholder,
  capsuleStyle,
  overflowBadgeStyle,
  truncate,
}: {
  selected: string[];
  options: { label: string; value: string }[];
  placeholder: string;
  capsuleStyle: React.CSSProperties;
  overflowBadgeStyle: React.CSSProperties;
  truncate: (text: string, max?: number) => string;
}) => {
  const visible = selected.slice(0, 3);
  const extraCount = selected.length - 3;

  if (selected.length === 0) {
    return (
      <span style={{ color: "#aaa", fontSize: 12, pointerEvents: "none" }}>
        {placeholder}
      </span>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 4,
        overflow: "hidden",
        flexWrap: "nowrap",
        pointerEvents: "none",
      }}
    >
      {visible.map((value) => {
        const opt = options.find((o) => String(o.value) === String(value));
        if (!opt) return null;
        return (
          <span key={value} style={capsuleStyle} title={opt.label}>
            {truncate(opt.label)}
          </span>
        );
      })}
      {extraCount > 0 && <span style={overflowBadgeStyle}>+{extraCount}</span>}
    </div>
  );
};


const LBMBrandMapping: React.FC = () => {
  /* ── State ── */
  const [segment, setSegment] = useState<SegmentType>("B2C");
  const [selectedConstruct, setSelectedConstruct] =
    useState<ConstructOption | null>(null);
  const [constructList, setConstructList] = useState<ConstructOption[]>([]);
  const [gridData, setGridData] = useState<GridRow[]>([]);
  const [lbmOptions, setLbmOptions] = useState<LBMOption[]>([]);
  const [productOptions, setProductOptions] = useState<ProductOption[]>([]);
  const [loader, setLoader] = useState<boolean>(false);
  const [constructLoading, setConstructLoading] = useState<boolean>(false);
  const [saveLoading, setSaveLoading] = useState<boolean>(false);
  const [uploadModalRow, setUploadModalRow] = useState<GridRow | null>(null);

  /* ── Constructs: reload on segment change ── */
  useEffect(() => {
    const load = async () => {
      setConstructLoading(true);
      setSelectedConstruct(null);
      setGridData([]);
      try {
        const res = await fetchData(
          controllerName,
          Endpoint.GetConstructData,
          `ChannelType=${CHANNEL_TYPE_MAP[segment]}`,
        );
        setConstructList(
          (res.Data ?? []).map((d: any) => ({
            label: d.Name,
            value: d.Seq,
            startDate: d.StartDate,
            endDate: d.EndDate,
          })),
        );
      } catch (error: any) {
        toast.error(
          error?.response?.data?.Message ?? "Failed to load constructs",
        );
      } finally {
        setConstructLoading(false);
      }
    };
    load();
  }, [segment]);

  /* ── LBM options: global, once on mount ── */
  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetchData(controllerName, Endpoint.GetAllLBMData, "");
        setLbmOptions(
          (res.Data ?? []).map((d: any) => ({
            label: d.LBMBrandDesc,
            value: d.LBMBrandCode,
          })),
        );
      } catch (error: any) {
        toast.error(
          error?.response?.data?.Message ?? "Failed to load LBM brands",
        );
      }
    };
    load();
  }, []);

  /* ── Product options: global, once on mount ── */
  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetchData(
          controllerName,
          Endpoint.GetAllProductByLBM,
          "",
        );
        const raw: any[] = res.Data ?? [];
        const mapped: ProductOption[] = raw
          .map((d: any) => ({
            label: String(d.ProductVariantName ?? ""),
            value: String(d.ProductVariantCode ?? ""),
          }))
          .filter((o) => o.value !== "");
        setProductOptions(mapped);
      } catch {
        // non-fatal
      }
    };
    load();
  }, []);

  /* ── Grid: reload on construct selection ── */
  useEffect(() => {
    if (!selectedConstruct) {
      setGridData([]);
      return;
    }
    fetchGridData();
  }, [selectedConstruct]);

  const fetchGridData = async () => {
    if (!selectedConstruct) return;
    setLoader(true);
    try {
      const res = await fetchData(
        controllerName,
        Endpoint.GetGridData,
        `ConstarctSeq=${selectedConstruct}&ChannelType=${CHANNEL_TYPE_MAP[segment]}`,
      );

      const rows: GridRow[] = (res.Data ?? []).map((d: any) => ({
        ConstructSeq: d.ConstructSeq,
        Name: d.Name,
        Seq: d.Seq,
        IS_SKU_SELECTED: d.IS_SKU_SELECTED,
        selectedLBMs: [],
        selectedProducts: [],
        skuCheckboxChecked: segment === "B2C" && d.IS_SKU_SELECTED === "Y",
        skuCodes: [],
      }));

      const hydrated = await Promise.all(
        rows.map(async (row) => {
          try {
            const [lbmRes, prodRes] = await Promise.all([
              fetchData(
                controllerName,
                Endpoint.GetSelectedLBM,
                `Seq=${row.Seq}&ConstarctSeq=${row.ConstructSeq}&ChannelType=${CHANNEL_TYPE_MAP[segment]}`,
              ),
              fetchData(
                controllerName,
                Endpoint.GetSelectedProduct,
                `Seq=${row.Seq}&ConstarctSeq=${row.ConstructSeq}&ChannelType=${CHANNEL_TYPE_MAP[segment]}`,
              ),
            ]);

            const selectedLBMs: string[] = (lbmRes.Data ?? []).map((d: any) =>
              String(d.LBMBrandCode ?? d.value ?? d),
            );
            const selectedProducts: string[] = (prodRes.Data ?? []).map(
              (d: any) => String(d.ProductVariantCode ?? d.value ?? d),
            );

            // If Upload SKU is checked, keep dropdowns empty
            if (row.skuCheckboxChecked) {
              return { ...row, selectedLBMs: [], selectedProducts: [] };
            }
            return { ...row, selectedLBMs, selectedProducts };
          } catch {
            return row;
          }
        }),
      );

      setGridData(hydrated);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.Message ?? "Failed to fetch grid data",
      );
    } finally {
      setLoader(false);
    }
  };

  /* ── Cell change handlers ── */
  const onLBMChange = useCallback((rowSeq: number, vals: string[]) => {
    setGridData((prev) =>
      prev.map((r) => (r.Seq === rowSeq ? { ...r, selectedLBMs: vals } : r)),
    );
  }, []);

  const onProductChange = useCallback((rowSeq: number, vals: string[]) => {
    setGridData((prev) =>
      prev.map((r) =>
        r.Seq === rowSeq ? { ...r, selectedProducts: vals } : r,
      ),
    );
  }, []);

  const onSkuCheckboxChange = useCallback(
    (rowSeq: number, checked: boolean) => {
      setGridData((prev) =>
        prev.map((r) =>
          r.Seq === rowSeq
            ? {
                ...r,
                skuCheckboxChecked: checked,
                // Clear LBM/Product selections when enabling SKU upload mode
                selectedLBMs: checked ? [] : r.selectedLBMs,
                selectedProducts: checked ? [] : r.selectedProducts,
                // Clear previously loaded SKU codes when disabling
                skuCodes: checked ? r.skuCodes : [],
              }
            : r,
        ),
      );
    },
    [],
  );

  /* ── Callback from UploadSKUModal: store parsed codes on the row ── */
  const onSkuCodesLoaded = useCallback((rowSeq: number, codes: SkuEntry[]) => {
    setGridData((prev) =>
      prev.map((r) => (r.Seq === rowSeq ? { ...r, skuCodes: codes } : r)),
    );
  }, []);

  /* ── Sync uploadModalRow when gridData changes ── */
  useEffect(() => {
    if (uploadModalRow) {
      const updated = gridData.find((r) => r.Seq === uploadModalRow.Seq);
      if (updated) setUploadModalRow(updated);
    }
  }, [gridData]);

  const handleRefresh = () => {
    setSelectedConstruct(null);
    setSegment("B2C");
    setGridData([]);
  };

  const handleSave = async () => {
    if (!selectedConstruct) {
      toast.warn("Please select a construct.");
      return;
    }
    if (gridData.length === 0) {
      toast.warn("No data to save.");
      return;
    }

    setSaveLoading(true);
    try {
      const payload = {
        constructSeq: String(selectedConstruct),
        userId: UserId ?? "",
        channelType: CHANNEL_TYPE_MAP[segment],
        parameters: gridData.map((r) => {
          // Resolve full LBM brand objects from selected codes
          const selectedBrands = r.selectedLBMs.map((code) => {
            const match = lbmOptions.find((o) => o.value === code);
            return {
              lbmBrandCode: code,
              lbmBrandDesc: match?.label ?? "",
            };
          });

          // Resolve full product objects from selected codes
          const selectedProducts = r.selectedProducts.map((code) => {
            const match = productOptions.find((o) => o.value === code);
            return {
              productVariantCode: code,
              productVariantName: match?.label ?? "",
            };
          });

          return {
            paramId: String(r.Seq),
            parameterName: r.Name,
            selectedBrands,
            selectedProducts,
            upload: r.skuCheckboxChecked ? r.skuCodes : [],
          };
        }),
      };

      const res = await postData(
        controllerName,
        Endpoint.SavingLBMProduct,
        payload,
      );
      toast.success(res?.Message ?? "Data saved successfully.");
    } catch (error: any) {
      toast.error(error?.response?.data?.message ?? "Save failed");
    } finally {
      setSaveLoading(false);
    }
  };

  const truncate = (text: string, max = 8) =>
    text.length > max ? text.slice(0, max) + "…" : text;

  const capsuleStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    border: "1px solid #2e7d32",
    borderRadius: 20,
    padding: "1px 8px",
    fontSize: 10,
    color: "#2e7d32",
    background: "#f1f8f1",
    maxWidth: 70,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    flexShrink: 0,
  };

  const overflowBadgeStyle: React.CSSProperties = {
    fontSize: 10,
    color: "#888",
    background: "#f0f0f0",
    border: "1px solid #ccc",
    borderRadius: 20,
    padding: "1px 6px",
    fontWeight: 600,
    flexShrink: 0,
  };

  // lbmBrandBody — wrapped in useCallback to keep a stable reference and
  // prevent the DataTable from unmounting/remounting the MultiSelect panel
  // on every gridData state update (which was closing the dropdown mid-selection).
  const lbmBrandBody = useCallback(
    (row: GridRow) => (
      <div style={{ position: "relative", width: "100%", minHeight: 32 }}>
        <MultiSelect
          value={row.selectedLBMs}
          options={lbmOptions}
          onChange={(e) => onLBMChange(row.Seq, e.value)}
          optionLabel="label"
          optionValue="value"
          placeholder=" "
          filter
          filterPlaceholder="Search..."
          disabled={row.skuCheckboxChecked}
          display="comma"
          className="w-100 custom-chip-multiselect"
          style={{ fontSize: 12 }}
          itemTemplate={(option) => (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: 12,
              }}
            >
              <span
                style={{
                  background: "#e8f5e9",
                  color: "#1b5e20",
                  borderRadius: 4,
                  padding: "1px 7px",
                  fontSize: 11,
                  fontWeight: 700,
                  minWidth: 42,
                  textAlign: "center",
                }}
              >
                {option.value}
              </span>
              <span>{option.label}</span>
            </div>
          )}
        />
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            height: "100%",
            minHeight: 32,
            width: "calc(100% - 36px)",
            display: "flex",
            alignItems: "center",
            padding: "0 8px",
            pointerEvents: "none",
            overflow: "hidden",
          }}
        >
          <ChipLabel
            selected={row.selectedLBMs}
            options={lbmOptions}
            placeholder="Select LBM Brand"
            capsuleStyle={capsuleStyle}
            overflowBadgeStyle={overflowBadgeStyle}
            truncate={truncate}
          />
        </div>
      </div>
    ),
    [lbmOptions, onLBMChange],
  );

  const productBody = useCallback(
    (row: GridRow) => (
      <div style={{ position: "relative", width: "100%", minHeight: 32 }}>
        <MultiSelect
          value={row.selectedProducts}
          options={productOptions}
          onChange={(e) => onProductChange(row.Seq, e.value)}
          optionLabel="label"
          optionValue="value"
          placeholder=" "
          filter
          filterPlaceholder="Search..."
          disabled={row.skuCheckboxChecked}
          display="comma"
          className="w-100 custom-chip-multiselect"
          style={{ fontSize: 12 }}
          itemTemplate={(option) => (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: 12,
              }}
            >
              <span>{option.label}</span>
            </div>
          )}
        />
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            height: "100%",
            minHeight: 32,
            width: "calc(100% - 36px)",
            display: "flex",
            alignItems: "center",
            padding: "0 8px",
            pointerEvents: "none",
            overflow: "hidden",
          }}
        >
          <ChipLabel
            selected={row.selectedProducts}
            options={productOptions}
            placeholder="Select Product"
            capsuleStyle={capsuleStyle}
            overflowBadgeStyle={overflowBadgeStyle}
            truncate={truncate}
          />
        </div>
      </div>
    ),
    [productOptions, onProductChange],
  );

  /* ── Column: Upload SKU ── */
  const uploadSKUBody = (row: GridRow) => {
    const isChecked = row.skuCheckboxChecked;
    const hasSkus = row.skuCodes.length > 0;

    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {/* Checkbox */}
        <input
          type="checkbox"
          checked={isChecked}
          onChange={(e) => onSkuCheckboxChange(row.Seq, e.target.checked)}
          title="Check to enable SKU upload and clear LBM/Product selections"
          style={{
            width: 16,
            height: 16,
            accentColor: "#2e7d32",
            cursor: "pointer",
            flexShrink: 0,
          }}
        />

        {/* Upload SKU button */}
        <button
          type="button"
          onClick={() => isChecked && setUploadModalRow(row)}
          disabled={!isChecked}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 5,
            padding: "4px 10px",
            borderRadius: 5,
            border: "none",
            fontWeight: 600,
            fontSize: 12,
            cursor: isChecked ? "pointer" : "not-allowed",
            transition: "background 0.2s, color 0.2s",
            background: isChecked ? "#2e7d32" : "#e0e0e0",
            color: isChecked ? "#fff" : "#999",
          }}
        >
          <Fa.FaUpload style={{ fontSize: 11 }} />
          Upload SKU
        </button>

        {/* Badge showing how many SKU codes are loaded */}
        {isChecked && hasSkus && (
          <span
            style={{
              background: "#e8f5e9",
              color: "#2e7d32",
              borderRadius: 10,
              padding: "2px 8px",
              fontSize: 11,
              fontWeight: 700,
              whiteSpace: "nowrap",
            }}
          >
            {row.skuCodes.length} SKU{row.skuCodes.length !== 1 ? "s" : ""}
          </span>
        )}
      </div>
    );
  };

  if (loader) return <Loader />;

  return (
    <main>
      {/* Upload SKU Modal */}
      {uploadModalRow && (
        <UploadSKUModal
          row={uploadModalRow}
          onClose={() => setUploadModalRow(null)}
          onSkuCodesLoaded={onSkuCodesLoaded}
        />
      )}

      <div className="col-12 mb-5">
        {/* Header */}
        <div className="row">
          <div className="col-lg-12">
            <nav aria-label="breadcrumb" className="drop-in">
              <ol className="breadcrumb mx-0">
                <li className="breadcrumb-item bold">DB SIP</li>
                <li className="breadcrumb-item active" aria-current="page">
                  LBM Brand and Brand Mapping
                </li>
              </ol>
            </nav>
          </div>
          <div className="col-lg-6 col-md-6 col-sm-12">
            <h3 className="py-2">LBM Brand and Brand Mapping</h3>
          </div>
          <div className="col-lg-6 col-md-6 col-sm-12">
            <div className="text-end">
              <button
                type="button"
                className="btn anime-secondary-btn me-2"
                onClick={handleRefresh}
              >
                Refresh <Fa.FaSync className="ms-2" />
              </button>
              <button
                type="button"
                className="btn anime-btn"
                onClick={handleSave}
                disabled={saveLoading}
              >
                {saveLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Fa.FaSave className="me-1" /> Save
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <section>
          <div className="sales-box">
            <div className="row d-flex justify-content-center align-items-center">
              <div className="col-lg-8 col-md-12 col-sm-12">
                <div className="d-flex justify-content-start flex-wrap gap-3">
                  {(["B2C", "HDSSD", "Industrial DB"] as SegmentType[]).map(
                    (s) => (
                      <div key={s} className="d-inline-flex align-items-center">
                        <RadioButton
                          inputId={`seg-${s}`}
                          name="segment"
                          value={s}
                          onChange={(e) => setSegment(e.value)}
                          checked={segment === s}
                        />
                        <label
                          htmlFor={`seg-${s}`}
                          className="form-label ms-2 mb-0"
                        >
                          {s}
                        </label>
                      </div>
                    ),
                  )}
                </div>
              </div>
              <div className="col-lg-4 col-md-12 col-sm-12">
                <div className="text-end">
                  <Dropdown
                    value={selectedConstruct}
                    onChange={(e) => setSelectedConstruct(e.value)}
                    options={constructList}
                    optionLabel="label"
                    placeholder={
                      constructLoading ? "Loading..." : "Select Construct"
                    }
                    className="flex-grow-1"
                    disabled={constructLoading}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Grid */}
        <div className="col-lg-12 col-md-12 col-sm-12">
          <h6>Records</h6>
          <div className="DataTable">
            <DataTable
              value={gridData}
              rows={10}
              rowsPerPageOptions={[10, 25, 50]}
              showGridlines
              paginator
              stripedRows
              scrollable
              // scrollHeight="60vh"
              tableStyle={{ minWidth: "50rem" }}
              emptyMessage={
                selectedConstruct
                  ? "No data found for the selected construct."
                  : "Select a construct to populate the data grid."
              }
            >
              <Column
                field="Name"
                header="Name"
                style={{ minWidth: "320px" }}
              />
              <Column
                header="LBM Brand"
                body={lbmBrandBody}
                style={{ minWidth: "160px", maxWidth: "320px" }}
              />
              <Column
                header="Product"
                body={productBody}
                style={{ minWidth: "160px", maxWidth: "280px" }}
              />
              {segment === "B2C" && (
                <Column
                  header="Upload SKU"
                  body={uploadSKUBody}
                  style={{ minWidth: "220px" }}
                />
              )}
            </DataTable>
          </div>
        </div>
      </div>
    </main>
  );
};

export default LBMBrandMapping;
